import * as THREE from "three";
import { toon } from "./toon";

/**
 * Shops you can see into along a street spot (#186): clear glass with a lit
 * room behind it, a shallow box the shopfront's width, furnished for what the
 * shop sells. Drawn only (the sim never sees the street), from a handful of
 * shared materials so the street's merge (`mergeStatic`) keeps them to a few
 * draw calls.
 */
export type InteriorKind = "cafe" | "clothes" | "pharmacy" | "books" | "diner" | "grocer";

/** Which shops (by `SHOPS` name) you can see into, and what's inside. */
export const INTERIORS: Readonly<Record<string, InteriorKind>> = {
  "BEAN THERE": "cafe",
  "BUTTON & BOW": "clothes",
  "CORNER PHARMACY": "pharmacy",
  "DOG-EARED": "books",
  "ROSIE'S DINER": "diner",
  "THE GREEN GROCER": "grocer",
};

/** The shop (by name) whose unit is empty: papered-over windows and a COMING SOON sign instead of its own. */
export const COMING_SOON = "FOCAL POINT";

/** How deep a shop's room goes behind its glass, and how high its ceiling is (the shopfront glass's top), in m. */
export const ROOM_DEPTH = 2.6;
export const ROOM_H = 2.85;

/** A room's depth by what's in it: the café runs deeper (#186 round 3), to seat more behind its wider front. */
export function roomDepth(kind: InteriorKind): number {
  return kind === "cafe" ? 4 : ROOM_DEPTH;
}

export interface InteriorMaterials {
  wall: THREE.Material;
  floor: THREE.Material;
  light: THREE.Material;
  wood: THREE.Material;
  dark: THREE.Material;
  metal: THREE.Material;
  white: THREE.Material;
  /** Goods: clothes, books, packets, produce. */
  goods: readonly THREE.Material[];
  /** The see-through shopfront glass. */
  glass: THREE.Material;
  /** Brown paper over an empty unit's windows, and the lighter sheets on it. */
  paper: THREE.Material;
  sheet: THREE.Material;
}

export function interiorMaterials(): InteriorMaterials {
  const glass = toon("#cfe3ee", { roughness: 0.05, metalness: 0.1, transparent: true, opacity: 0.22, depthWrite: false }, "glass");
  return {
    // Lit from within: warm walls that read through the glass from the street.
    wall: toon("#efe2c8", { roughness: 0.9, emissive: "#f3dcb0", emissiveIntensity: 0.45 }, "paint"),
    floor: toon("#8a6a4f", { roughness: 0.7, emissive: "#3a2a1c", emissiveIntensity: 0.3 }, "plywood"),
    light: new THREE.MeshBasicMaterial({ color: "#fff3cf" }),
    wood: toon("#9a6b43", { roughness: 0.8, emissive: "#3a2a1c", emissiveIntensity: 0.25 }, "plywood"),
    dark: toon("#2f2a28", { roughness: 0.6, emissive: "#1a1614", emissiveIntensity: 0.3 }, "paint"),
    metal: toon("#b9c0c8", { roughness: 0.4, metalness: 0.6 }, "metal"),
    white: toon("#f4f2ec", { roughness: 0.7, emissive: "#d8d4ca", emissiveIntensity: 0.35 }, "paint"),
    goods: ["#c8463c", "#3d7cc9", "#e8b84a", "#4f9a5a", "#8a5ab8"].map((c) => toon(c, { roughness: 0.8, emissive: c, emissiveIntensity: 0.2 }, "cloth")),
    glass,
    paper: toon("#c4a272", { roughness: 1 }, "paint"),
    sheet: toon("#e8dfc6", { roughness: 1 }, "paint"),
  };
}

/** Places boxes and cylinders in a room's frame: `u` along the street from its middle, `y` up from the floor, `d` in from the glass. */
class Room {
  readonly parts: THREE.Mesh[] = [];
  constructor(
    readonly w: number,
    private readonly m: InteriorMaterials,
    readonly depth = ROOM_DEPTH,
  ) {}

  box(mat: THREE.Material, u: number, y: number, d: number, su: number, sy: number, sd: number, name = ""): THREE.Mesh {
    const mesh = new THREE.Mesh(new THREE.BoxGeometry(su, sy, sd), mat);
    mesh.position.set(u, y + sy / 2, d);
    mesh.name = name;
    this.parts.push(mesh);
    return mesh;
  }

  cyl(mat: THREE.Material, u: number, y: number, d: number, r: number, h: number, name = ""): THREE.Mesh {
    const mesh = new THREE.Mesh(new THREE.CylinderGeometry(r, r, h, 10), mat);
    mesh.position.set(u, y + h / 2, d);
    mesh.name = name;
    this.parts.push(mesh);
    return mesh;
  }

  /** A row of goods `n` wide on a shelf at height `y`, from `u0` to `u1`, `d` in, each `h` tall. */
  goods(u0: number, u1: number, y: number, d: number, h: number, depth: number, seed: number): void {
    const n = Math.max(2, Math.round((u1 - u0) / 0.32));
    const step = (u1 - u0) / n;
    for (let i = 0; i < n; i++) {
      const mat = this.m.goods[(i * 3 + seed) % this.m.goods.length]!;
      this.box(mat, u0 + step * (i + 0.5), y, d, step * 0.85, h * (0.75 + 0.25 * (((i + seed) * 7) % 3) / 2), depth);
    }
  }

  /** A shelving unit along the back wall: uprights, `rows` shelves, goods on each. */
  backShelves(u0: number, u1: number, rows: number, seed: number): void {
    const d = this.depth - 0.25;
    const m = this.m;
    this.box(m.wood, (u0 + u1) / 2, 0, d, u1 - u0, 0.08, 0.4);
    for (let r = 0; r < rows; r++) {
      const y = 0.45 + r * 0.45;
      this.box(m.wood, (u0 + u1) / 2, y - 0.03, d, u1 - u0, 0.03, 0.4);
      this.goods(u0 + 0.05, u1 - 0.05, y, d, 0.3, 0.3, seed + r);
    }
  }
}

const FURNISH: Record<InteriorKind, (r: Room, m: InteriorMaterials) => void> = {
  cafe(r, m) {
    const w = r.w;
    const D = r.depth;
    // The counter along the back on the left, a long one (#186 round 3), its top dark.
    const c0 = -w * 0.45;
    const c1 = -w * 0.05;
    const cd = D - 1;
    r.box(m.wood, (c0 + c1) / 2, 0, cd, c1 - c0, 1, 0.65);
    r.box(m.dark, (c0 + c1) / 2, 1, cd, c1 - c0 + 0.06, 0.04, 0.69);
    const top = 1.04;
    // The espresso machine on it, facing the window: a red body with a
    // chrome front, three group heads with their handles, a steam wand each
    // end, the drip tray, and cups stacked on top to keep warm.
    const eu = c0 + (c1 - c0) * 0.3;
    const front = cd - 0.3;
    r.box(m.goods[0]!, eu, top, cd, 1.2, 0.55, 0.6, "espresso machine");
    r.box(m.metal, eu, top + 0.08, front - 0.01, 1.1, 0.42, 0.02);
    r.box(m.metal, eu, top, front - 0.12, 1.1, 0.03, 0.22, "drip tray");
    for (const k of [-1, 0, 1]) {
      r.cyl(m.metal, eu + k * 0.34, top + 0.2, front - 0.08, 0.06, 0.1, "group head");
      r.box(m.dark, eu + k * 0.34, top + 0.2, front - 0.24, 0.05, 0.04, 0.26, "group head");
    }
    for (const s of [-1, 1]) r.cyl(m.metal, eu + s * 0.52, top + 0.06, front - 0.07, 0.015, 0.34, "steam wand");
    for (let i = 0; i < 5; i++) for (let j = 0; j < 2; j++) r.cyl(m.white, eu - 0.4 + i * 0.2, top + 0.55 + j * 0.08, cd - 0.1 + (i % 2) * 0.16, 0.05, 0.08, "cup");
    // The grinder beside it: a dark body and its bean hopper on top.
    r.box(m.dark, eu + 0.9, top, cd, 0.26, 0.42, 0.3, "grinder");
    r.cyl(m.metal, eu + 0.9, top + 0.42, cd, 0.11, 0.24, "grinder");
    // The cake case and the till further along.
    r.box(m.white, c0 + (c1 - c0) * 0.62, top, cd, 1.4, 0.32, 0.5);
    r.box(m.dark, c0 + (c1 - c0) * 0.88, top, cd, 0.35, 0.22, 0.3);
    // Kraft bags of beans on a shelf behind, and the menu boards over it: chalk lines on black.
    r.box(m.wood, (c0 + c1) / 2, 1.3, D - 0.15, c1 - c0, 0.03, 0.25);
    for (let u = c0 + 0.25; u < c1 - 0.2; u += 0.4) r.box(m.paper, u, 1.33, D - 0.15, 0.22, 0.3, 0.14);
    for (const k of [-1, 0, 1]) {
      const u = (c0 + c1) / 2 + k * (c1 - c0) * 0.3;
      r.box(m.dark, u, 1.75, D - 0.04, 1.6, 0.75, 0.04);
      for (let i = 0; i < 4; i++) r.box(m.white, u - 0.15 + (i % 2) * 0.1, 1.85 + i * 0.15, D - 0.07, 1 - (i % 2) * 0.3, 0.035, 0.02);
    }
    // Tables and chairs: a row along the window, another row on the right behind it.
    const n = Math.max(2, Math.floor(w / 3.2));
    const step = w / n;
    for (let i = 0; i < n; i++) {
      const u = -w / 2 + step * (i + 0.5);
      for (const d of u >= 0 ? [0.85, D - 1.6] : [0.85]) {
        r.cyl(m.dark, u, 0, d, 0.04, 0.72);
        r.cyl(m.wood, u, 0.72, d, 0.32, 0.04, "café table");
        for (const s of [-1, 1]) {
          r.box(m.dark, u + s * 0.5, 0, d, 0.38, 0.45, 0.38);
          r.box(m.dark, u + s * 0.67, 0.45, d, 0.04, 0.45, 0.38);
        }
      }
    }
    // Shelves of mugs and bags of beans to take home, on the back wall on the right.
    const s0 = w * 0.08;
    const s1 = w * 0.45;
    for (let k = 0; k < 3; k++) {
      const y = 0.45 + k * 0.45;
      r.box(m.wood, (s0 + s1) / 2, y - 0.03, D - 0.25, s1 - s0, 0.03, 0.4);
      for (let u = s0 + 0.15; u < s1 - 0.1; u += 0.3) {
        if (k === 1) r.box(m.paper, u, y, D - 0.25, 0.2, 0.28, 0.12);
        else r.cyl(m.white, u, y, D - 0.25, 0.06, 0.11);
      }
    }
  },
  clothes(r, m) {
    const w = r.w;
    // Two rails of clothes on stands, and folded stacks on shelves at the back.
    for (const u of [-w * 0.22, w * 0.18]) {
      for (const s of [-1, 1]) r.box(m.metal, u + s * 0.7, 0, 1.0, 0.04, 1.45, 0.04);
      r.box(m.metal, u, 1.42, 1.0, 1.44, 0.03, 0.03);
      for (let i = 0; i < 8; i++) r.box(m.goods[(i + (u > 0 ? 2 : 0)) % m.goods.length]!, u - 0.6 + i * 0.17, 0.6, 1.0, 0.05, 0.8, 0.45);
    }
    r.backShelves(-w * 0.4, w * 0.4, 3, 1);
  },
  pharmacy(r, m) {
    const w = r.w;
    // White shelving along the back, two aisles of shelves, the counter on the right.
    r.box(m.white, 0, 0, ROOM_DEPTH - 0.25, w * 0.8, 2.1, 0.06);
    r.backShelves(-w * 0.4, w * 0.4, 4, 2);
    // An aisle shelf facing the window, goods on each of its shelves.
    r.box(m.white, -w * 0.15, 0, 1.25, w * 0.4, 1.45, 0.5);
    for (let k = 0; k < 3; k++) r.goods(-w * 0.34, w * 0.04, 0.3 + k * 0.42, 0.95, 0.26, 0.12, k + 2);
    r.box(m.white, w * 0.3, 0, 1.4, 1.4, 1, 0.55);
    r.box(m.goods[3]!, w * 0.3, 1, 1.4, 1.42, 0.05, 0.57);
  },
  books(r, m) {
    const w = r.w;
    // Bookcases floor to ceiling along the back, a table of stacks in front.
    r.backShelves(-w * 0.42, w * 0.42, 5, 0);
    r.box(m.wood, 0, 0, 1.0, 1.6, 0.75, 0.8);
    for (let i = 0; i < 5; i++) r.box(m.goods[i % m.goods.length]!, -0.6 + i * 0.3, 0.75, 1.0, 0.22, 0.1 + (i % 3) * 0.06, 0.3);
  },
  diner(r, m) {
    const w = r.w;
    // The counter along the back with stools, booths in the window.
    r.box(m.white, 0, 0, ROOM_DEPTH - 0.55, w * 0.7, 1, 0.5);
    r.box(m.goods[0]!, 0, 1, ROOM_DEPTH - 0.55, w * 0.7 + 0.05, 0.05, 0.55);
    for (let i = 0; i < 5; i++) {
      const u = -w * 0.3 + (i * w * 0.6) / 4;
      r.cyl(m.metal, u, 0, ROOM_DEPTH - 1.05, 0.03, 0.7);
      r.cyl(m.goods[0]!, u, 0.7, ROOM_DEPTH - 1.05, 0.18, 0.08);
    }
    for (const u of [-w * 0.25, w * 0.15]) {
      r.box(m.white, u, 0, 0.6, 0.8, 0.74, 0.55);
      for (const s of [-1, 1]) r.box(m.goods[0]!, u + s * 0.65, 0, 0.6, 0.4, 1.1, 0.6);
    }
  },
  grocer(r, m) {
    const w = r.w;
    // Crates of produce on stands in rows, and shelves along the back.
    r.backShelves(-w * 0.4, w * 0.4, 3, 3);
    for (const d of [0.7, 1.5]) {
      for (let i = 0; i < 4; i++) {
        const u = -w * 0.3 + (i * w * 0.6) / 3;
        r.box(m.wood, u, 0, d, 0.7, 0.75, 0.5);
        r.box(m.goods[(i * 2 + (d > 1 ? 1 : 0)) % m.goods.length]!, u, 0.75, d, 0.62, 0.12, 0.42);
      }
    }
  },
};

/**
 * A shop's room behind its glass, `w` wide, in its own frame (`u` along the
 * street from the middle, `y` up, `d` in from the glass): lined walls, a
 * floor, ceiling lights, and what the shop has in it.
 */
export function shopInterior(kind: InteriorKind, w: number, m: InteriorMaterials): THREE.Mesh[] {
  const D = roomDepth(kind);
  const r = new Room(w, m, D);
  r.box(m.floor, 0, 0, D / 2, w, 0.02, D);
  r.box(m.wall, 0, 0, D - 0.01, w, ROOM_H, 0.02);
  for (const s of [-1, 1]) r.box(m.wall, s * (w / 2 - 0.01), 0, D / 2, 0.02, ROOM_H, D);
  r.box(m.wall, 0, ROOM_H - 0.02, D / 2, w, 0.02, D);
  for (const d of [0.8, 1.9, 3].filter((d) => d < D - 0.5)) r.box(m.light, 0, ROOM_H - 0.06, d, w * 0.6, 0.03, 0.14);
  FURNISH[kind](r, m);
  return r.parts;
}

/** An empty unit's papered-over window, `w` wide and `h` tall: brown paper and a few lighter sheets taped on, `d` out toward the street from the glass's line. */
export function paperedWindow(w: number, h: number, m: InteriorMaterials): THREE.Mesh[] {
  const r = new Room(w, m);
  r.box(m.paper, 0, 0, 0, w, h, 0.02);
  const sheets: [number, number, number, number][] = [
    [-0.3, 0.35, 0.28, 0.4],
    [0.05, 0.15, 0.24, 0.5],
    [0.32, 0.5, 0.22, 0.36],
  ];
  for (const [u, y, sw, sh] of sheets) r.box(m.sheet, u * w, y * h, 0.02, sw * w, sh * h, 0.01);
  return r.parts;
}
